import React, { useState, useEffect, useRef } from 'react';

interface Notification {
  id: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  aoi_id?: string;
  aoi_name?: string;
  change_metrics?: {
    change_percentage?: number;
    changed_area?: number;
    total_area?: number;
    change_types?: Array<{ type: string; area: number; percentage: number }>;
    // aircraft_activity / vessel_activity notifications (aoi_scheduler.py) carry
    // these instead of change_percentage/changed_area.
    aircraft_count?: number;
    new_aircraft?: number;
    vessel_count?: number;
    new_vessels?: number;
  };
  created_at: string;
  read: boolean;
}

interface GEOINTNotificationsProps {
  // notificationType lets the parent auto-trigger a live aircraft/vessel check for
  // the AOI when the click came from an aircraft_activity/vessel_activity alert,
  // instead of just flying to the AOI and waiting for a manual click.
  onAOISelect?: (aoiId: string, notificationType?: string) => void;
}

const severityColors: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500' },
  high: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500' },
  medium: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500' },
  low: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', badge: 'bg-green-500' },
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500' },
};

const GEOINTNotifications: React.FC<GEOINTNotificationsProps> = ({ onAOISelect }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
      const response = await fetch('/api/geoint/wayback/notifications?limit=30', {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch GEOINT notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
      await fetch(`/api/geoint/wayback/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
      await fetch('/api/geoint/wayback/notifications/mark-all-read', {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all read:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) fetchNotifications();
        }}
        className="relative p-2 rounded-md hover:bg-muted transition-colors"
        title="GEOINT Notifications"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-muted-foreground"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-card border border-border rounded-lg shadow-xl z-50 max-h-[500px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">GEOINT Alerts</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-primary hover:underline"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={fetchNotifications}
                className="text-xs text-muted-foreground hover:text-foreground"
                title="Refresh"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                  <path d="M16 16h5v5" />
                </svg>
              </button>
            </div>
          </div>

          {/* Notifications list */}
          <div className="flex-1 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-muted-foreground text-sm">No notifications</div>
                <div className="text-muted-foreground/60 text-xs mt-1">
                  Alerts will appear here when changes are detected in your monitored AOIs
                </div>
              </div>
            ) : (
              notifications.map((notification) => {
                const colors = severityColors[notification.severity] || severityColors.info;
                return (
                  <div
                    key={notification.id}
                    className={`p-3 border-b border-border/50 cursor-pointer hover:bg-muted/50 transition-colors ${
                      !notification.read ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => {
                      if (!notification.read) markRead(notification.id);
                      if (notification.aoi_id && onAOISelect) {
                        onAOISelect(notification.aoi_id, notification.type);
                        setIsOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-start gap-2">
                      {/* Severity badge */}
                      <span
                        className={`${colors.badge} text-white text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5 uppercase tracking-wider shrink-0`}
                      >
                        {notification.severity}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground truncate">
                            {notification.title}
                          </span>
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        {notification.change_metrics?.change_percentage !== undefined && (
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className={`text-xs font-mono ${colors.text}`}>
                              {notification.change_metrics.change_percentage?.toFixed(1)}% changed
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {notification.change_metrics.changed_area?.toFixed(2)} km²
                            </span>
                          </div>
                        )}
                        {notification.change_metrics?.aircraft_count !== undefined && (
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className={`text-xs font-mono ${colors.text}`}>
                              +{notification.change_metrics.new_aircraft} new aircraft
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {notification.change_metrics.aircraft_count} total in AOI
                            </span>
                          </div>
                        )}
                        {notification.change_metrics?.vessel_count !== undefined && (
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className={`text-xs font-mono ${colors.text}`}>
                              +{notification.change_metrics.new_vessels} new vessel(s)
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {notification.change_metrics.vessel_count} total in AOI
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Unread dot */}
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GEOINTNotifications;
